import { createSelector } from '@reduxjs/toolkit';

import { isolatedDialog, isolatedHeader, type IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys';
import { selectedVehicleSelector } from 'Core/reducers/fitmentSearch/storage';
import type { ReduxStateType, Selector } from 'Core/store';
import { type FacetCollection, FacetValue, Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { hasRequiredVehicleSelection, isVehicleChangedOnResponse } from 'Models/vehicle';
import { getFitmentResponseFacets, getRequiredFitmentFields } from 'Modules/fitmentSearchFacets';
import { createFacetCollectionSelector, createIsolatedSelectorCreator } from 'Modules/selectors';
import { equalBy } from 'Utils/array';

import { fitmentSearchSelector } from './fitmentSearch';

type FitmentSearchSlice = Pick<ReduxStateType['fitmentSearch'], 'fields' | 'request' | 'response'>;
type FitmentSearchIsolatedKey = Exclude<IsolatedKey, null>;

const emptyFields: string[] = [];

function isFitmentSearchIsolatedKey(key: string | null): key is FitmentSearchIsolatedKey {
  return key === isolatedDialog || key === isolatedHeader;
}

function createRootSelector(isolatedKey: string | null = null): Selector<FitmentSearchSlice> {
  return (state) => {
    const fitmentSearchState = fitmentSearchSelector(state);
    return isFitmentSearchIsolatedKey(isolatedKey) ? fitmentSearchState[isolatedKey] : fitmentSearchState;
  };
}

const createFitmentSearchBaseFieldsSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) => {
      return rootSelector(state).fields.base;
    },
);

export const createFitmentSearchExtraFieldsSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) =>
      rootSelector(state).fields.extra,
);

export const createFitmentSearchFieldsSelector = createIsolatedSelectorCreator(
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchExtraFieldsSelector,
  (selectors) => createSelector(selectors, (base: string[], extra: string[]) => [...base, ...extra]),
);

const createIsolatedRequestSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) =>
      rootSelector(state).request,
);

export const createFitmentSearchRequestSelectionSelector = createIsolatedSelectorCreator(
  createIsolatedRequestSelector,
  ([requestSelector]) =>
    (state: ReduxStateType) =>
      requestSelector(state).selection,
);

const createIsolatedResponseSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) =>
      rootSelector(state).response,
);

export const createFitmentSearchResponseAllFacetsSelector = createIsolatedSelectorCreator(
  createIsolatedResponseSelector,
  (selectors) => createFacetCollectionSelector(selectors, (response) => response.facets),
);

const createFilteredFitmentSearchResponseSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseAllFacetsSelector,
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchExtraFieldsSelector,
  (selectors, isolatedKey: string | null) =>
    createSelector(
      selectors,
      (facets: FacetCollection, fitmentBaseFields: string[], fitmentExtraFields: string[]) => {
        const filteredFacets = getFitmentResponseFacets(facets, fitmentBaseFields, fitmentExtraFields, {
          hideExtraFields:
            !fitmentSearchConfig.isExtraFieldsAlwaysVisible &&
            (fitmentSearchConfig.isOnCategorySelectionPage ||
              isolatedKey === isolatedHeader ||
              (isolatedKey === isolatedDialog && !requestConfig.hasSearchResults)),
          isPartialMode: fitmentSearchConfig.isVehicleWidgetInPartialMode,
        });
        return { facets: filteredFacets, fields: filteredFacets.map((facet) => facet.field) };
      },
    ),
);

export const createFitmentSearchResponseFacetsSelector = createIsolatedSelectorCreator(
  createFilteredFitmentSearchResponseSelector,
  (selectors) => createFacetCollectionSelector(selectors, (response) => response.facets),
);

export const createFilteredFitmentSearchResponseFieldsSelector = createIsolatedSelectorCreator(
  createFilteredFitmentSearchResponseSelector,
  (selectors) => createSelector(selectors, (response) => response.fields),
);

export const createRequiredFitmentSearchFieldsSelector = createIsolatedSelectorCreator(
  createFilteredFitmentSearchResponseFieldsSelector,
  createFitmentSearchBaseFieldsSelector,
  (selectors) => createSelector(selectors, getRequiredFitmentFields),
);

export const createFitmentSearchResponseSelectionSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseFacetsSelector,
  (selectors) => createSelector(selectors, (facets) => facets.selection),
);

export const createFitmentSearchSelectionChangedSelector = createIsolatedSelectorCreator(
  createFitmentSearchRequestSelectionSelector,
  createFitmentSearchResponseSelectionSelector,
  (selectors) =>
    createSelector(
      selectors,
      (requestSelection, responseSelection) =>
        !equalBy(requestSelection, responseSelection, FacetValue.termKey),
    ),
);

export const createAllBaseFieldsHaveSelectionSelector = createIsolatedSelectorCreator(
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  ([baseFieldsSelector, selectionSelector]) =>
    (state: ReduxStateType) => {
      return hasRequiredVehicleSelection(selectionSelector(state), baseFieldsSelector(state));
    },
);

export const createAllRequiredFieldsHaveSelectionSelector = createIsolatedSelectorCreator(
  createRequiredFitmentSearchFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  ([requiredFieldsSelector, selectionSelector]) =>
    (state: ReduxStateType) => {
      return hasRequiredVehicleSelection(selectionSelector(state), requiredFieldsSelector(state));
    },
);

export const createResponseVehicleSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseSelectionSelector,
  createFitmentSearchFieldsSelector,
  (selectors) =>
    createSelector(selectors, (selection, fitmentFields) => new Vehicle(selection, fitmentFields)),
);

export const createIsServerChangedVehicleSelector = createIsolatedSelectorCreator(
  createResponseVehicleSelector,
  createAllRequiredFieldsHaveSelectionSelector,
  createFitmentSearchFieldsSelector,
  ([responseVehicleSelector, isAllRequiredFieldHasSelectionSelector, fitmentFieldsSelector]) =>
    createSelector(
      [
        selectedVehicleSelector,
        responseVehicleSelector,
        isAllRequiredFieldHasSelectionSelector,
        fitmentFieldsSelector,
        (_state: ReduxStateType, fields?: string[]) => fields ?? emptyFields,
      ],
      (
        selectedVehicle: Vehicle,
        responseVehicle: Vehicle,
        isAllRequiredFieldHasSelection: boolean,
        allFields: string[],
        widgetFields: string[],
      ) => {
        const fitmentFields = widgetFields?.length ? widgetFields : allFields;
        const selectedFilteredVehicle = new Vehicle(selectedVehicle.fieldMap, fitmentFields);
        const responseFilteredVehicle = new Vehicle(responseVehicle.fieldMap, fitmentFields);

        return (
          selectedFilteredVehicle.notNull &&
          isVehicleChangedOnResponse(
            selectedFilteredVehicle,
            responseFilteredVehicle,
            isAllRequiredFieldHasSelection,
          )
        );
      },
    ),
);
